<script setup lang="ts">
import { usePublicSiteRepository } from '~/repositories/publicSiteRepository'

/**
 * Tenant news article — metadata header, block body, "more from" rail.
 * Fires the view ping once per slug per browser session.
 */
definePageMeta({ layout: false, middleware: ['tenant'] })

const tenant = useTenant()
if (tenant.tenantMode.value === 'platform') {
  throw createError({ statusCode: 404, statusMessage: 'Page not found', fatal: true })
}
const activeTenant = computed(() => tenant.tenant.value)

const route = useRoute()
const repo = usePublicSiteRepository()
const slug = String(route.params.newsSlug)

const { data: response, error } = await useAsyncData(
  `site-news-${slug}`,
  () => repo.getArticle(slug)
)

if (error.value || !response.value?.data) {
  throw createError({ statusCode: 404, statusMessage: 'Post not found', fatal: true })
}

const post = computed(() => response.value!.data)
const moreFrom = computed(() => response.value!.more_from ?? [])

const { t } = useI18n()
const siteDate = useSiteDate()
const dateLabel = computed(() => siteDate.long(post.value.published_at))

onMounted(() => {
  const key = `site-news-viewed:${slug}`
  if (!sessionStorage.getItem(key)) {
    sessionStorage.setItem(key, '1')
    repo.trackView(slug).catch(() => {})
  }
})

useSeoMeta({
  title: () => post.value.seo.meta_title || `${post.value.title} — ${activeTenant.value?.name ?? ''}`,
  description: () => post.value.seo.meta_description || post.value.excerpt || undefined,
  ogTitle: () => post.value.seo.meta_title || post.value.title,
  ogDescription: () => post.value.seo.meta_description || post.value.excerpt || undefined,
  ogImage: () => post.value.seo.og_image_url || post.value.cover_image_url || undefined,
  ogType: 'article'
})
</script>

<template>
  <SiteShell
    v-if="activeTenant"
    :tenant="activeTenant"
  >
    <article class="py-12 sm:py-16">
      <header class="mx-auto max-w-3xl px-6">
        <NuxtLink
          to="/news"
          class="site-text-accent text-sm font-medium"
        >
          ← {{ t('site.news_title') }}
        </NuxtLink>
        <div class="mt-4 flex flex-wrap items-center gap-2 text-sm text-[var(--site-muted)]">
          <span
            v-if="post.category"
            class="site-bg-accent-soft site-text-accent rounded-full px-2.5 py-0.5 text-xs font-medium"
          >{{ post.category.name }}</span>
          <span v-if="dateLabel">{{ dateLabel }}</span>
          <span
            v-if="post.view_count"
            class="inline-flex items-center gap-1"
          >
            <UIcon
              name="i-lucide-eye"
              class="size-3.5"
            />{{ post.view_count }} {{ t('site.news_views') }}
          </span>
        </div>
        <h1 class="site-display mt-3 text-3xl leading-tight tracking-tight sm:text-5xl">
          {{ post.title }}
        </h1>
        <p
          v-if="post.excerpt"
          class="mt-4 text-lg leading-relaxed text-[var(--site-muted)]"
        >
          {{ post.excerpt }}
        </p>
      </header>

      <div
        v-if="post.cover_image_url"
        class="mx-auto mt-8 max-w-4xl px-6"
      >
        <img
          :src="post.cover_image_url"
          :alt="post.title"
          class="w-full rounded-[var(--site-radius)] object-cover"
        >
      </div>

      <div class="mt-4">
        <SiteBlockRenderer :blocks="post.blocks" />
      </div>

      <!-- More from -->
      <aside
        v-if="moreFrom.length"
        class="mx-auto mt-14 max-w-5xl border-t border-[var(--site-border)] px-6 pt-10"
      >
        <h2 class="site-display text-2xl tracking-tight">
          {{ t('site.news_more_from', { name: post.category?.name || activeTenant.name }) }}
        </h2>
        <div class="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <SiteNewsCard
            v-for="item in moreFrom"
            :key="item.slug"
            :post="item"
          />
        </div>
      </aside>
    </article>
  </SiteShell>
</template>
